import { useCallback, useState } from "react";

import { SubscriptionStatus } from "@prisma/client";

import Button, { type IButtonProps } from "@musicfy/components/Button";
import Dialog from "@musicfy/components/Dialog";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";

import CancellationWizard from "./CancellationWizard";
import { type Reason } from "./constants";

const CancelSubscriptionButton: React.FC<IButtonProps> = ({
  ...buttonProps
}) => {
  const { trackEvent } = useAnalytics();
  const { sendToastMessage } = useToastContext();

  const { subscription, renewSubscription, resumeSubscription } =
    useSubscriptionContext();

  const [isRenewalDialogOpen, setIsRenewalDialogOpen] = useState(false);
  const [isResumeDialogOpen, setIsResumeDialogOpen] = useState(false);
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] =
    useState(false);

  const [isRenewing, setIsRenewing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  const [cancellationReason, setCancellationReason] = useState<Reason | string>(
    ""
  );

  const handleCancelSubscriptionClick = useCallback(() => {
    if (!subscription) {
      return;
    }
    setIsCancellationDialogOpen(true);
    trackEvent("Subscription Cancellation Wizard Viewed", {
      plan: subscription?.plan,
      interval: subscription?.interval,
    });
  }, [subscription, trackEvent]);

  const onRenewSubscription = useCallback(async () => {
    if (!subscription) {
      return;
    }
    setIsRenewing(true);
    try {
      await renewSubscription();
      setIsRenewalDialogOpen(false);
      sendToastMessage("Your subscription will continue as usual.", {
        severity: "success",
      });
    } catch (error) {
      const message =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message: unknown }).message === "string"
          ? (error as { message: string }).message
          : "Unable to renew your subscription. Please try again.";
      sendToastMessage(message, { severity: "error" });
    } finally {
      setIsRenewing(false);
    }
  }, [renewSubscription, sendToastMessage, subscription]);

  const onResumeSubscription = useCallback(async () => {
    if (!subscription) {
      return;
    }

    setIsResuming(true);
    try {
      await resumeSubscription();
      setIsResumeDialogOpen(false);
      setIsCancellationDialogOpen(false);
    } catch (error) {
      console.error(error);
      sendToastMessage(
        "Unable to resume your subscription. Please try again.",
        {
          severity: "error",
        }
      );
    } finally {
      setIsResuming(false);
    }
  }, [resumeSubscription, sendToastMessage, subscription]);

  if (!subscription) {
    return null;
  }

  const isSubscriptionSetToCancel = !!subscription.canceledAt;
  const isSubscriptionPaused =
    subscription.status === SubscriptionStatus.paused;

  if (isSubscriptionSetToCancel) {
    const renewalDate = subscription?.periodEnd
      ? new Date(subscription.periodEnd).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

    return (
      <>
        <Button
          variant="text"
          style="tertiary"
          {...buttonProps}
          onClick={() => setIsRenewalDialogOpen(true)}
        >
          Keep Subscription
        </Button>
        <Dialog
          open={isRenewalDialogOpen}
          onClose={() => setIsRenewalDialogOpen(false)}
          maxWidth="sm"
          title="Keep Subscription"
          subtitle={`Undo cancellation and keep your current plan? You will be billed again on ${
            renewalDate || "your next billing date"
          }.`}
          primaryButton={
            <Button
              onClick={onRenewSubscription}
              fullWidth
              loading={isRenewing}
            >
              Keep Subscription
            </Button>
          }
          secondaryButton={
            <Button
              style="solid"
              className="!bg-gray-900"
              onClick={() => setIsRenewalDialogOpen(false)}
              fullWidth
            >
              Cancel
            </Button>
          }
        />
      </>
    );
  }

  if (isSubscriptionPaused) {
    return (
      <>
        <Button
          variant="text"
          style="tertiary"
          {...buttonProps}
          onClick={() => setIsResumeDialogOpen(true)}
        >
          Resume Subscription
        </Button>
        <Dialog
          open={isResumeDialogOpen}
          onClose={() => setIsResumeDialogOpen(false)}
          maxWidth="sm"
          title="Resume Subscription"
          subtitle={`Are you sure you want to resume your subscription? Your subscription will continue to renew automatically.`}
          primaryButton={
            <Button
              onClick={() => onResumeSubscription()}
              fullWidth
              loading={isResuming}
            >
              Resume Subscription
            </Button>
          }
          secondaryButton={
            <Button
              style="solid"
              className="!bg-gray-900"
              onClick={() => setIsResumeDialogOpen(false)}
              fullWidth
            >
              Cancel
            </Button>
          }
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="text"
        style="tertiary"
        onClick={() => handleCancelSubscriptionClick()}
        {...buttonProps}
      >
        Cancel Subscription
      </Button>
      <CancellationWizard
        isOpen={isCancellationDialogOpen}
        onClose={() => setIsCancellationDialogOpen(false)}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
      />
    </>
  );
};

export default CancelSubscriptionButton;
